import { TOOLS } from '../Replan/constants/drawingTools';

/**
 * Build assembly params from a shape, including calculated measurement params.
 *
 * @param {Object} shape - The shape object (must have .type, .params, and possibly .radius).
 * @param {Object} measurementData - Result of getMeasurementDataForShape(shape).
 * @param {Object} [options={}] - Optional overrides.
 * @param {Function} [options.getCalculatedKey] - Optional mapper for calculated param keys,
 *        e.g. (key) => t(key). If not provided, hardcoded English keys are used.
 * @returns {{ assemblyParams: Object, calculatedKeys: string[] }}
 */
export function buildAssemblyParams(shape, measurementData, options = {}) {
  const { getCalculatedKey } = options;

  const mapKey = (hardcoded) =>
    typeof getCalculatedKey === 'function' ? getCalculatedKey(hardcoded) : hardcoded;

  const calculatedParams = {};

  if (shape.type === TOOLS.POLYLINE) {
    calculatedParams[mapKey('Total Length')] =
      measurementData.summary.replace('Total: ', '');
  }
  if (shape.type === TOOLS.AREA) {
    calculatedParams[mapKey('Total Area')] = measurementData.summary
      .replace('Net Area: ', '')
      .replace('Area: ', '');
  }
  if (shape.type === TOOLS.POINT) {
    const key = mapKey('Diameter');
    const diameterDetail = measurementData?.details?.find(
      (d) => typeof d === 'string' && d.startsWith('Diameter:'),
    );
    if (diameterDetail) {
      calculatedParams[key] = diameterDetail.replace('Diameter: ', '');
    } else {
      const radius = shape.radius || 0;
      calculatedParams[key] = (radius * 2).toFixed(2);
    }
  }

  // Merge user params with calculated params
  const assemblyParams = {
    ...(shape.params || {}),
    ...calculatedParams,
  };

  // Ensure the order array includes the calculated param keys
  const calculatedKeys = Object.keys(calculatedParams);
  const existingOrder = Array.isArray(assemblyParams.order)
    ? [...assemblyParams.order]
    : [];
  calculatedKeys.forEach((key) => {
    if (!existingOrder.includes(key)) {
      existingOrder.push(key);
    }
  });
  if (existingOrder.length > 0) {
    assemblyParams.order = existingOrder;
  }

  return { assemblyParams, calculatedKeys };
}

/**
 * Save an assembly from a shape to Firebase.
 * Validates the name, builds params, creates or updates the assembly doc.
 *
 * @param {Object} opts
 * @param {Object}  opts.firebase                      - Firebase instance
 * @param {string}  opts.organization                  - Organization ID
 * @param {string}  opts.projectName                   - Project name
 * @param {Object}  opts.shape                         - Shape object (must have .params.Assembly)
 * @param {Function} opts.getMeasurementDataForShape    - (shape) => measurementData
 * @param {string}  [opts.actorUid]                    - User UID for activity log
 * @param {string}  [opts.actorEmail]                  - User email for activity log
 * @param {Function} [opts.t]                          - i18n translate function (fallback returns default)
 * @param {Object}  [opts.buildOptions]                - Options forwarded to buildAssemblyParams
 * @returns {Promise<{success: boolean, status: string}>}
 */
export async function saveAssemblyFromShape({
  firebase,
  organization,
  projectName,
  shape,
  getMeasurementDataForShape,
  actorUid = null,
  actorEmail = null,
  t = (key, fallback) => fallback || key,
  buildOptions = {},
}) {
  try {
    const assemblyName = shape.params?.Assembly;
    if (!assemblyName) {
      console.warn('saveAssemblyFromShape: shape has no Assembly param');
      return { success: false, status: 'no_assembly_param' };
    }

    // Validate name
    if (typeof firebase.assemblyNameError === 'function') {
      const nameErr = firebase.assemblyNameError(assemblyName);
      if (nameErr) {
        alert(nameErr);
        return { success: false, status: 'invalid_name' };
      }
    }

    const measurementData = getMeasurementDataForShape(shape);
    const { assemblyParams } = buildAssemblyParams(shape, measurementData, buildOptions);

    try {
      await firebase.doCreateAssembly(
        organization,
        projectName,
        assemblyName,
        assemblyParams,
        assemblyName,
        actorUid,
        actorEmail,
      );
      return { success: true, status: 'created' };
    } catch (createError) {
      if (createError.code === 'ASSEMBLY_ALREADY_EXISTS') {
        const shouldUpdate = window.confirm(
          `Assembly "${assemblyName}" already exists. Do you want to update it with the current shape's parameters?`,
        );
        if (shouldUpdate) {
          await firebase.doUpdateAssembly(
            organization,
            projectName,
            assemblyName,
            assemblyParams,
            assemblyName,
            actorUid,
            actorEmail,
          );
          return { success: true, status: 'updated' };
        }
        return { success: false, status: 'cancelled' };
      }
      if (createError.code === 'INVALID_ASSEMBLY_NAME') {
        alert(createError.message);
        return { success: false, status: 'invalid_name' };
      }
      throw createError;
    }
  } catch (error) {
    console.error('Error saving assembly from shape:', error);
    alert(
      t('shapeList.failed_save_assembly', `Failed to save assembly: ${error.message}`),
    );
    return { success: false, status: 'error' };
  }
}
